import { Feature, FeatureCollection } from 'geojson';
import { TagVariant } from '@/components/molecules/tag/tag';
import { HotelCardProps } from '@/components/organisms/hotel-card/hotel-card';

export const destinationMapHotelCardCollectionAdapter = (
  items?: Content.Properties.MapHotelEntry[],
): HotelCardProps[] => {
  if (!items) return [];

  return items.map(item => {
    const tags = [
      ...item.facilityHighlights.map(value => ({
        text: value,
        variant: 'primary' as TagVariant,
      })),
      ...item.distances.map(value => ({
        text: value,
        variant: 'secondary' as TagVariant,
      })),
    ];

    return {
      id: String(item.hotelId),
      image: item.picture
        ? {
            altText: item.picture.description ?? '',
            url: item.picture.url,
          }
        : undefined,
      name: item.hotelName,
      rating: item.stars ?? undefined,
      tags,
      totalPricePerPerson: item.bestPrice?.totalPricePerPerson ?? undefined,
      type: item.hotelType,
      url: item.url,
    };
  });
};

export const destinationMapHotelsFeatureAdapter = (
  item: Content.Properties.MapHotelEntry,
  currencyFormatter: (amount: number) => string,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.geoLocation?.longitude ?? 0,
        item.geoLocation?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      id: String(item.hotelId),
      markerLabel:
        item.bestPrice?.totalPricePerPerson && currencyFormatter
          ? currencyFormatter(item.bestPrice?.totalPricePerPerson)
          : item.hotelName,
      name: item.hotelName,
      type: item.pointOfInterestType,
    },
    type: 'Feature',
  };
};

export const destinationMapPoisFeatureAdapter = (
  item: Content.Properties.MapPoiEntry,
): Feature => {
  return {
    geometry: {
      coordinates: [
        item.geoLocation?.longitude ?? 0,
        item.geoLocation?.latitude ?? 0,
      ],
      type: 'Point',
    },
    properties: {
      filterLabel: String(item.filterLabel),
      id: String(item.id),
      name: item.name,
      poiLabel: item.name,
      type: item.pointOfInterestType,
    },
    type: 'Feature',
  };
};

export const destinationMapHotelsFeatureCollectionAdapter = (
  currencyFormatter: (amount: number) => string,
  items?: Content.Properties.MapHotelEntry[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items.map(item =>
      destinationMapHotelsFeatureAdapter(item, currencyFormatter),
    ),
    type: 'FeatureCollection',
  };
};

export const destinationMapPoisFeatureCollectionAdapter = (
  items?: Content.Properties.MapPoiEntry[],
): FeatureCollection => {
  if (!items) {
    return {
      features: [],
      type: 'FeatureCollection',
    };
  }

  return {
    features: items.map(item => destinationMapPoisFeatureAdapter(item)),
    type: 'FeatureCollection',
  };
};

export function isDestinationMapHotel(
  item: Content.Properties.DestinationMapItem,
): item is Content.Properties.MapHotelEntry {
  return item.$type === 'MapHotelEntry';
}

export function isDestinationMapPoi(
  item: Content.Properties.DestinationMapItem,
): item is Content.Properties.MapPoiEntry {
  return item.$type === 'MapPoiEntry';
}
